import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useT } from "../i18n";
import { wingChunTopics } from "../data/wingchunTopics";

const Header = () => {
  const { lang, toggle } = useLanguage();
  const t = useT(lang);
  const [open, setOpen] = useState(false);
  const [wcOpen, setWcOpen] = useState(false);
  const [mobileWcOpen, setMobileWcOpen] = useState(false);

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/sifu", label: t.nav.sifu, testid: "nav-sifu" },
    { to: "/classes", label: t.nav.classes, testid: "nav-classes" },
  ];

  const rightLinks = [
    { to: "/book", label: t.nav.bookPage, testid: "nav-book" },
    { to: "/reviews", label: t.nav.reviews, testid: "nav-reviews" },
    { to: "/schedule", label: t.nav.schedule, testid: "nav-schedule" },
    { to: "/gallery", label: t.nav.gallery, testid: "nav-gallery" },
    { to: "/blog", label: t.nav.blog, testid: "nav-blog" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="container-wc flex items-center justify-between py-5">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
          <span className="font-cn text-gold text-3xl leading-none">詠春</span>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-xl tracking-wide">Wing Chun</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-dim">Sifu N. Agathopoulos</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}

          {/* Wing Chun dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setWcOpen(true)}
            onMouseLeave={() => setWcOpen(false)}
          >
            <NavLink
              to="/wing-chun"
              data-testid="nav-wingchun"
              className={({ isActive }) => `nav-link flex items-center gap-1 ${isActive ? "active" : ""}`}
            >
              {t.nav.wingchun}
              <ChevronDown size={14} className={`transition-transform duration-200 ${wcOpen ? "rotate-180" : ""}`} />
            </NavLink>
            <div
              className={`absolute left-0 top-full pt-3 min-w-[280px] transition-all duration-200 ${
                wcOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"
              }`}
            >
              <div className="bg-[#0d0d0d]/95 backdrop-blur-md border border-goldSoft py-2 shadow-2xl">
                {wingChunTopics.map((topic) => (
                  <NavLink
                    key={topic.slug}
                    to={`/wing-chun/${topic.slug}`}
                    data-testid={`nav-wc-${topic.slug}`}
                    className={({ isActive }) =>
                      `block px-5 py-3 text-sm tracking-wide transition-colors border-l-2 ${
                        isActive
                          ? "text-gold border-gold bg-gold/5"
                          : "text-dim border-transparent hover:text-gold hover:border-gold hover:bg-gold/5"
                      }`
                    }
                  >
                    {lang === "el" ? topic.label_el : topic.label_en}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          {rightLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="text-xs uppercase tracking-widest border border-goldSoft px-3 py-2 hover:border-gold hover:text-gold transition-colors"
            aria-label="Toggle language"
          >
            {lang === "el" ? "EN" : "EL"}
          </button>
          <Link to="/contact" data-testid="header-book-btn" className="hidden md:inline-flex btn-primary">
            {t.nav.book}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-gold"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-goldSoft bg-black/95 max-h-[80vh] overflow-y-auto">
          <div className="container-wc py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`m-${l.testid}`}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                onClick={() => setOpen(false)}
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}

            {/* Mobile Wing Chun accordion */}
            <div>
              <button
                data-testid="m-nav-wingchun"
                onClick={() => setMobileWcOpen(!mobileWcOpen)}
                className="nav-link flex items-center justify-between w-full"
              >
                {t.nav.wingchun}
                <ChevronDown size={16} className={`transition-transform ${mobileWcOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileWcOpen && (
                <div className="pl-4 mt-2 space-y-2 border-l border-goldSoft">
                  <NavLink
                    to="/wing-chun"
                    onClick={() => setOpen(false)}
                    className="block text-sm text-dim hover:text-gold py-1"
                    data-testid="m-nav-wc-overview"
                  >
                    {lang === "el" ? "Επισκόπηση" : "Overview"}
                  </NavLink>
                  {wingChunTopics.map((topic) => (
                    <NavLink
                      key={topic.slug}
                      to={`/wing-chun/${topic.slug}`}
                      onClick={() => setOpen(false)}
                      className="block text-sm text-dim hover:text-gold py-1"
                      data-testid={`m-nav-wc-${topic.slug}`}
                    >
                      {lang === "el" ? topic.label_el : topic.label_en}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {rightLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`m-${l.testid}`}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                onClick={() => setOpen(false)}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="m-header-book-btn" onClick={() => setOpen(false)} className="btn-primary mt-2 self-start">
              {t.nav.book}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
